// Auth API Module - Consolidated authentication related APIs
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { LoginMethod } from "../../types/auth";
import { ChangePasswordFormData, UserDetailsResponse } from "../../types/user";
import api from "../axios";
import { ApiResponse, handleApiResponse } from "../handleApiResponse";

// Login
export async function loginUser(payload: {
  loginMethod: LoginMethod;
  identifier: string;
  password: string;
}): Promise<ApiResponse<any>> {
  let loginPayload: Record<string, string> = { password: payload.password };
  if (payload.loginMethod === LoginMethod.EMAIL) {
    loginPayload.email = payload.identifier;
  } else if (payload.loginMethod === LoginMethod.PHONE) {
    loginPayload.phone = payload.identifier;
  } else if (payload.loginMethod === LoginMethod.AADHAR) {
    loginPayload.aadhar = payload.identifier;
  } else {
    return {
      success: false,
      data: null,
      message: "Invalid login method",
    };
  }
  return handleApiResponse(api.post(API_ENDPOINTS.AUTH.LOGIN, loginPayload));
}

// Registration
export async function registerUser(payload: {
  loginMethod: LoginMethod;
  name: string;
  email?: string;
  phone?: string;
  aadhar?: string;
  password: string;
  referralCode?: string;
}): Promise<ApiResponse<any>> {
  const { loginMethod, name, email, phone, aadhar, password, referralCode } =
    payload;
  const data: any = { loginMethod, name, password };
  if (loginMethod === LoginMethod.EMAIL && email) data.email = email;
  if (loginMethod === LoginMethod.PHONE && phone) data.phone = phone;
  if (loginMethod === LoginMethod.AADHAR && aadhar) data.aadhar = aadhar;
  if (referralCode) data.referralCode = referralCode;
  return handleApiResponse(api.post(API_ENDPOINTS.AUTH.REGISTER, data));
}

// Email Verification
export async function verifyEmail(token: string): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.post(API_ENDPOINTS.AUTH.VERIFY_EMAIL, { token })
  );
}

// Forgot Password - Request reset link
export async function forgotPassword(email: string): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email })
  );
}

// Reset Password - Complete reset with token
export async function resetPassword(payload: {
  token: string;
  password: string;
}): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload)
  );
}

export async function resetUserPassword(
  userId: string,
  newPassword: string
): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.patch(API_ENDPOINTS.USERS.RESET_PASSWORD(userId), { newPassword })
  );
}

// Change Password
export async function changePassword(
  payload: ChangePasswordFormData
): Promise<ApiResponse<any>> {
  return handleApiResponse(
    api.patch(API_ENDPOINTS.USERS.CHANGE_PASSWORD, payload)
  );
}

export async function fetchCurrentUser(): Promise<
  ApiResponse<UserDetailsResponse>
> {
  // First get the current user's id from the standardized API response
  const meRes = await handleApiResponse(api.get(API_ENDPOINTS.AUTH.ME));
  // Extend the type to allow both _id and id
  const userData = meRes.data as Partial<UserDetailsResponse> & { id?: string };
  const userId = userData?._id || userData?.id;
  if (!meRes.success || !userId) {
    return {
      success: false,
      data: null as any,
      message: meRes.message || "User ID not found in /v1/auth/me response",
    };
  }
  // Then fetch user details by id
  return handleApiResponse(api.get(API_ENDPOINTS.USERS.BY_ID(userId)));
}
